import { useCallback, useEffect, useRef, useState } from 'react'
import { EVENTS } from '@sh/shared'
import type { ClientAction, PlayerView, PublicView } from '@sh/shared'
import { getSocket, request } from './socket.js'
import { clearSeat, loadSeat, saveSeat } from './session.js'

export type ConnectionState = 'connecting' | 'live' | 'offline' | 'rejected'

/**
 * Subscribes to the private view for a seat, resuming from the stored token on
 * every (re)connect. Every reconnect re-authenticates; the server re-sends the
 * redacted view rather than the client caching secrets.
 */
export function usePlayerGame(code: string) {
  const [view, setView] = useState<PlayerView | null>(null)
  const [status, setStatus] = useState<ConnectionState>('connecting')
  const [error, setError] = useState<string | null>(null)
  const resuming = useRef(false)

  useEffect(() => {
    const socket = getSocket()

    const resume = async () => {
      if (resuming.current) return
      const seat = loadSeat(code)
      if (!seat) {
        setStatus('rejected')
        setError('no seat saved on this device')
        return
      }
      resuming.current = true
      const ack = await request<{ playerId: string }>(EVENTS.resume, {
        code,
        playerId: seat.playerId,
        token: seat.token,
      })
      resuming.current = false
      if (ack.ok) {
        setStatus('live')
        setError(null)
      } else {
        setStatus('rejected')
        setError(ack.error)
      }
    }

    const onView = (msg: { view: PlayerView }) => {
      setView(msg.view)
      setStatus('live')
    }
    const onDisconnect = () => setStatus('offline')
    /*
     * Both, because they are different failures and only one of them used to
     * be listened for. `disconnect` fires when a connection that existed goes
     * away; a cold load against a server that is down never connects at all,
     * so it fires `connect_error` over and over and `disconnect` never once.
     * With only the first of them wired up, a phone opened while the server
     * was unreachable sat on `connecting` for as long as anyone was willing to
     * watch it, and the screen had nothing truthful it could say.
     */
    const onConnectError = () => setStatus('offline')

    socket.on(EVENTS.view, onView)
    socket.on('connect', resume)
    socket.on('disconnect', onDisconnect)
    socket.on('connect_error', onConnectError)
    if (socket.connected) void resume()

    return () => {
      socket.off(EVENTS.view, onView)
      socket.off('connect', resume)
      socket.off('disconnect', onDisconnect)
      socket.off('connect_error', onConnectError)
    }
  }, [code])

  const act = useCallback(async (action: ClientAction) => {
    const ack = await request<null>(EVENTS.action, action)
    if (!ack.ok) setError(ack.error)
    else setError(null)
    return ack
  }, [])

  const leave = useCallback(() => {
    clearSeat(code)
  }, [code])

  return { view, status, error, act, setError, leave }
}

/** Table screens: public state only, no credentials, any number of them. */
export function useBoardGame(code: string) {
  const [view, setView] = useState<PublicView | null>(null)
  const [error, setError] = useState<string | null>(null)
  /*
   * The same three states a phone reports, minus `rejected`: a table screen
   * authenticates with nothing, so the only way it is turned away is a lobby
   * code that names no game, and that arrives as an `error` rather than as a
   * state. It is here so the screen in front of a room can say which of "we
   * have not heard yet" and "we cannot reach anything" is true, which is the
   * whole difference between waiting and being stuck.
   */
  const [status, setStatus] = useState<ConnectionState>('connecting')

  useEffect(() => {
    const socket = getSocket()
    const subscribe = async () => {
      setStatus('live')
      const ack = await request<{ code: string }>(EVENTS.boardJoin, { code })
      if (!ack.ok) setError(ack.error)
      else setError(null)
    }
    const onBoard = (msg: { view: PublicView }) => setView(msg.view)
    const onDisconnect = () => setStatus('offline')
    const onConnectError = () => setStatus('offline')

    socket.on(EVENTS.board, onBoard)
    socket.on('connect', subscribe)
    socket.on('disconnect', onDisconnect)
    socket.on('connect_error', onConnectError)
    if (socket.connected) void subscribe()

    return () => {
      socket.off(EVENTS.board, onBoard)
      socket.off('connect', subscribe)
      socket.off('disconnect', onDisconnect)
      socket.off('connect_error', onConnectError)
    }
  }, [code])

  return { view, error, status }
}

export { saveSeat }
